import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import { getOrganizationRootContentId } from '@gitbook/app-hive';
import { useHiveClient } from '@gitbook/hive-react';
import { Icon as GitBookIcon, type IconName as GitBookIconName } from '@gitbook/icons';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbItem,
    Breadcrumbs,
    DataTableSkeleton,
    Description,
    Dialog,
    DialogBody,
    HeaderBar,
    HeaderBarBreadcrumbs,
    HeaderBarMain,
    Heading,
    IconTile,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    LoadingPanel,
    Tab,
    Tabs,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { useAgent } from '../Agent';
import { AgentChat } from '../AgentChat';
import { BasicDocument } from '../BasicEditor/BasicDocument';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { ClusterChangeRequestsTable } from './ClusterChangeRequestsTable';
import { ConversationIssueImpactSpan } from './ConversationIssueImpactSpan';
import { ConversationIssuesTable } from './ConversationIssuesTable';
import { ConversationsIssuesClusterToggle } from './ConversationsIssuesClusterToggle';

type ConversationsIssuesClusterDialogProps = {
    organizationId: string;
    clusterId: string;
};

/**
 * Dialog to inspect a cluster.
 */
export function ConversationsIssuesClusterDialog(props: ConversationsIssuesClusterDialogProps) {
    return (
        <Dialog
            size={{
                width: 1280,
                height: 800,
            }}
        >
            <React.Suspense fallback={<LoadingPanel />}>
                <ConversationsIssuesClusterDialogContent {...props} />
            </React.Suspense>
        </Dialog>
    );
}

function ConversationsIssuesClusterDialogContent(props: ConversationsIssuesClusterDialogProps) {
    const { organizationId, clusterId } = props;

    const [tab, setTab] = React.useState<'issues' | 'change_requests'>('issues');
    const { data: cluster } = useSuspenseQuery(
        apiQuery('getConversationsIssuesCluster', { organizationId, clusterId })
    );

    return (
        <div className="flex h-full w-full flex-row">
            <div className="flex flex-1 flex-col gap-0">
                <DialogBody className="flex flex-1 flex-col gap-6">
                    <div className="flex items-center gap-3">
                        <IconTile className="size-12">
                            <GitBookIcon
                                icon={cluster.icon as GitBookIconName}
                                className="size-8 text-muted"
                            />
                        </IconTile>
                        <div className="flex flex-1 flex-col">
                            <Heading>{cluster.label}</Heading>
                            <Description>
                                Identified{' '}
                                <DateShortRelative value={cluster.updatedAt} onlyDate={false} /> on{' '}
                                {cluster.stats.conversations} conversations.
                            </Description>
                        </div>
                        <ConversationsIssuesClusterToggle
                            organizationId={organizationId}
                            cluster={cluster}
                        />
                    </div>

                    <LabeledContainer>
                        <LabeledContainerHeader>
                            <LabeledContainerActionableTitle>
                                <Heading>Summary</Heading>
                            </LabeledContainerActionableTitle>
                        </LabeledContainerHeader>
                        <LabeledContainerBody>
                            <div className="flex flex-row items-center gap-2">
                                <p className="text-secondary text-ui-base">Estimated impact:</p>
                                <ConversationIssueImpactSpan impact={cluster.impact} />
                            </div>
                            <BasicDocument
                                document={cluster.summary}
                                typographyClassName="text-secondary"
                            />
                        </LabeledContainerBody>
                    </LabeledContainer>
                    <div className="flex flex-col gap-3">
                        <Tabs>
                            <Tab
                                label="Related issues"
                                active={tab === 'issues'}
                                onPress={() => {
                                    setTab('issues');
                                }}
                            />
                            <Tab
                                label="Change requests"
                                active={tab === 'change_requests'}
                                onPress={() => {
                                    setTab('change_requests');
                                }}
                            />
                        </Tabs>
                        <div>
                            {tab === 'issues' ? (
                                <React.Suspense fallback={<DataTableSkeleton />}>
                                    <ConversationIssuesTable
                                        organizationId={organizationId}
                                        clusterId={clusterId}
                                        withClusterRelation={false}
                                    />
                                </React.Suspense>
                            ) : null}
                            {tab === 'change_requests' ? (
                                <React.Suspense fallback={<DataTableSkeleton />}>
                                    <ClusterChangeRequestsTable
                                        organizationId={organizationId}
                                        clusterId={clusterId}
                                    />
                                </React.Suspense>
                            ) : null}
                        </div>
                    </div>
                </DialogBody>
            </div>
            <div className="flex flex-1 flex-col border-l">
                <HeaderBar>
                    <HeaderBarMain>
                        <HeaderBarBreadcrumbs>
                            <Breadcrumbs>
                                <BreadcrumbItem icon={Icon.Bulb} label="Thinking logs" />
                            </Breadcrumbs>
                        </HeaderBarBreadcrumbs>
                    </HeaderBarMain>
                </HeaderBar>
                <ConversationsIssuesClusterChat {...props} />
            </div>
        </div>
    );
}

function ConversationsIssuesClusterChat(props: ConversationsIssuesClusterDialogProps) {
    const { organizationId, clusterId } = props;

    const hiveClient = useHiveClient();
    const agent = useAgent({
        pathname: `/agents/o/${organizationId}/analyst/${clusterId}`,
        getToken: async () =>
            hiveClient.auth.getToken(getOrganizationRootContentId(organizationId)),
    });

    return (
        <ContentReferenceContextProvider
            value={{
                organization: organizationId,
            }}
        >
            <AgentChat agent={agent} withInput={false} />
        </ContentReferenceContextProvider>
    );
}
